<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useLanguageStore } from '@/stores/language';
import { useDirection } from '@/composables/useDirection';
import { useNotifications } from '@/composables/useNotifications_old';
import NotificationService from '@/service/Project/NotificationService_old';

const props = defineProps({
    class: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['close']);

const { t, currentLanguage } = useLanguageStore();
const { isRtl } = useDirection();
const { unreadCount, fetchUnreadCount, setUnreadCount } = useNotifications();

const options = computed(() => [
    { name: t('Inbox'), value: 'inbox' },
    { name: t('Archived'), value: 'archived' }
]);
const selectedOption = ref('inbox');

const notifications = ref([]);
const loading = ref(false);
const markingAllRead = ref(false);

const fetchNotifications = async () => {
    loading.value = true;
    try {
        const response = await NotificationService.getNotifications(selectedOption.value, 1, 15);
        if (response.success) {
            notifications.value = response.data.notifications || [];
            if (response.data.unread_count !== undefined) {
                setUnreadCount(response.data.unread_count);
            }
        }
    } catch (error) {
        console.error('Error fetching notifications:', error);
        notifications.value = [];
    } finally {
        loading.value = false;
    }
};

watch(selectedOption, () => {
    fetchNotifications();
});

// ── Push: prepend new notification when WebSocket event fires ──────────────
const onNotificationReceived = (event) => {
    const data = event.detail;
    if (!data || selectedOption.value !== 'inbox') return;
    // Only prepend if not already in list
    if (!notifications.value.find(n => n.id === data.id)) {
        notifications.value.unshift({
            ...data,
            is_read: false,
            created_at_human: t('Just now'),
            project: null,
            triggered_by: null,
        });
    }
};

// ── Refresh on visibility (IntersectionObserver) — no polling ─────────────
const dropdownRef = ref(null);
let observer = null;

onMounted(() => {
    fetchNotifications();
    window.addEventListener('notification:received', onNotificationReceived);

    if (dropdownRef.value?.$el || dropdownRef.value) {
        const el = dropdownRef.value.$el || dropdownRef.value;
        observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    fetchNotifications();
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(el);
    }
});

onUnmounted(() => {
    window.removeEventListener('notification:received', onNotificationReceived);
    if (observer) {
        observer.disconnect();
        observer = null;
    }
});

// Get notification icon based on type
const getNotificationIcon = (notification) => {
    const typeIconMap = {
        'escalation_created': 'pi pi-exclamation-triangle',
        'escalation_escalated': 'pi pi-arrow-up',
        'escalation_auto_escalated': 'pi pi-clock',
        'escalation_reminder': 'pi pi-bell',
        'escalation_closed': 'pi pi-check-circle',
        'project_created': 'pi pi-folder-plus',
        'project_updated': 'pi pi-pencil',
        'project_approved': 'pi pi-check',
        'project_rejected': 'pi pi-times',
        'project_deleted': 'pi pi-trash',
        'project_closed': 'pi pi-lock',
        'project_cancelled': 'pi pi-ban',
        'project_on_hold': 'pi pi-pause-circle',
        'phase1_submitted': 'pi pi-send',
        'phase1_approved': 'pi pi-check-circle',
        'phase1_rejected': 'pi pi-times-circle',
        'planning_pending': 'pi pi-send',
        'planning_approved': 'pi pi-check-circle',
        'planning_rejected': 'pi pi-times-circle',
        'pmo_pending': 'pi pi-send',
        'pmo_approved': 'pi pi-check-circle',
        'pmo_rejected': 'pi pi-times-circle',
        'board_pending': 'pi pi-send',
        'board_approved': 'pi pi-check-circle',
        'board_rejected': 'pi pi-times-circle',
        'resource_added': 'pi pi-user-plus',
        'resource_removed': 'pi pi-user-minus',
        'risk_added': 'pi pi-shield',
        'risk_updated': 'pi pi-shield',
        'issue_added': 'pi pi-flag',
        'issue_resolved': 'pi pi-flag-fill',
        'change_requested': 'pi pi-sync',
        'change_approved': 'pi pi-check-square',
        'change_rejected': 'pi pi-times-circle',
        'document_uploaded': 'pi pi-file',
        'benefit_added': 'pi pi-star',
        'lesson_learned_added': 'pi pi-book',
        'stakeholder_added': 'pi pi-users',
        'financial_item_added': 'pi pi-wallet',
        'financial_item_approved': 'pi pi-money-bill',
        'financial_item_rejected': 'pi pi-money-bill',
        'meeting_created': 'pi pi-calendar-plus',
        'meeting_approved': 'pi pi-check-circle',
        'meeting_rejected': 'pi pi-times-circle',
        // Strategy notifications
        'strategic_plan_submitted': 'pi pi-send',
        'strategic_plan_approved': 'pi pi-check-circle',
        'strategic_plan_rejected': 'pi pi-times-circle',
        'strategic_plan_auto_approved': 'pi pi-check-circle',
        'strategic_kpi_submitted': 'pi pi-send',
        'strategic_kpi_approved': 'pi pi-check-circle',
        'strategic_kpi_rejected': 'pi pi-times-circle',
        'strategic_kpi_auto_approved': 'pi pi-check-circle',
        'kpi_measurement_submitted': 'pi pi-send',
        'kpi_measurement_approved': 'pi pi-check-circle',
        'kpi_measurement_rejected': 'pi pi-times-circle',
        'kpi_measurement_auto_approved': 'pi pi-check-circle',
        // Initiative notifications
        'initiative_submitted': 'pi pi-send',
        'initiative_approved': 'pi pi-check-circle',
        'initiative_rejected': 'pi pi-times-circle',
        'initiative_auto_approved': 'pi pi-check-circle',
        // Initiative Update notifications
        'initiative_update_submitted': 'pi pi-send',
        'initiative_update_auto_approved': 'pi pi-check-circle',
        // Initiative Closure notifications
        'initiative_closure_submitted': 'pi pi-send',
        'initiative_closure_approved': 'pi pi-check-circle',
        'initiative_closure_rejected': 'pi pi-times-circle',
        'initiative_closure_auto_approved': 'pi pi-check-circle',
        // Initiative Change Request notifications
        'initiative_change_request_submitted': 'pi pi-send',
        'initiative_change_request_approved': 'pi pi-check-circle',
        'initiative_change_request_rejected': 'pi pi-times-circle',
        'initiative_change_request_auto_approved': 'pi pi-check-circle',
        // Strategic Risk notifications
        'strategic_risk_submitted': 'pi pi-send',
        'strategic_risk_approved': 'pi pi-check-circle',
        'strategic_risk_rejected': 'pi pi-times-circle',
        'strategic_risk_auto_approved': 'pi pi-check-circle',
        // Strategic Challenge notifications
        'strategic_challenge_submitted': 'pi pi-send',
        'strategic_challenge_approved': 'pi pi-check-circle',
        'strategic_challenge_rejected': 'pi pi-times-circle',
        'strategic_challenge_auto_approved': 'pi pi-check-circle',
        // Indicator Corrective Plan notifications
        'corrective_plan_submitted': 'pi pi-send',
        'corrective_plan_approved': 'pi pi-check-circle',
        'corrective_plan_rejected': 'pi pi-times-circle',
        'corrective_plan_auto_approved': 'pi pi-check-circle',
        // Program notifications
        'program_created': 'pi pi-folder-plus',
        'program_submitted': 'pi pi-send',
        'program_approved': 'pi pi-check-circle',
        'program_rejected': 'pi pi-times-circle',
        'program_auto_approved': 'pi pi-check-circle',
        'program_deleted': 'pi pi-trash',
        // Gate Approval notifications
        'gate_submitted': 'pi pi-send',
        'gate_approved': 'pi pi-check-circle',
        'gate_rejected': 'pi pi-times-circle',
        'gate_resubmitted': 'pi pi-replay',
    };
    return typeIconMap[notification.type] || 'pi pi-info-circle';
};

// Get icon background color
const getIconBgClass = (notification) => {
    const actionColorMap = {
        'warning': 'bg-orange-500',
        'warn': 'bg-orange-500',
        'success': 'bg-green-500',
        'error': 'bg-red-500',
        'info': 'bg-blue-500',
        'confirmation': 'bg-blue-500',
    };
    if (notification.action && actionColorMap[notification.action]) {
        return actionColorMap[notification.action];
    }
    const priorityColorMap = {
        'urgent': 'bg-red-500',
        'high': 'bg-orange-500',
        'medium': 'bg-blue-500',
        'low': 'bg-gray-400',
    };
    return priorityColorMap[notification.priority] || 'bg-blue-500';
};

// Get triggered by display name
const getTriggeredByName = (notification) => {
    return notification.triggered_by?.name || null;
};

// Mark single notification as read
const markAsRead = async (notification) => {
    if (notification.is_read) return;
    try {
        const response = await NotificationService.markAsRead(notification.id);
        if (response.success) {
            notification.is_read = true;
            if (response.data?.unread_count !== undefined) {
                setUnreadCount(response.data.unread_count);
            } else {
                fetchUnreadCount();
            }
        }
    } catch (error) {
        console.error('Error marking notification as read:', error);
    }
};

// Mark all as read
const markAllAsRead = async () => {
    markingAllRead.value = true;
    try {
        const response = await NotificationService.markAllAsRead();
        if (response.success) {
            notifications.value.forEach(n => n.is_read = true);
            setUnreadCount(0);
        }
    } catch (error) {
        console.error('Error marking all as read:', error);
    } finally {
        markingAllRead.value = false;
    }
};

// Archive / Unarchive
const archiveNotification = async (notification) => {
    try {
        if (selectedOption.value === 'archived') {
            await NotificationService.unarchive(notification.id);
        } else {
            await NotificationService.archive(notification.id);
        }
        notifications.value = notifications.value.filter(n => n.id !== notification.id);
    } catch (error) {
        console.error('Error archiving notification:', error);
    }
};

// Delete notification
const deleteNotification = async (notification) => {
    try {
        await NotificationService.delete(notification.id);
        notifications.value = notifications.value.filter(n => n.id !== notification.id);
        if (!notification.is_read) {
            fetchUnreadCount();
        }
    } catch (error) {
        console.error('Error deleting notification:', error);
    }
};

// Handle notification click — mark as read only, no navigation
const handleNotificationClick = async (notification) => {
    await markAsRead(notification);
};

// Priority badge class
const getPriorityClass = (priority) => {
    const map = {
        'urgent': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        'high': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
        'medium': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        'low': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    };
    return map[priority] || map['medium'];
};

// Get localized title/message based on current language
const ARABIC_TITLES = {
    'project_created': 'تم إنشاء مشروع جديد',
    'project_updated': 'تم تحديث المشروع',
    'project_deleted': 'تم حذف المشروع',
    'project_approved': 'تم اعتماد المشروع',
    'project_rejected': 'تم رفض المشروع',
    'project_closed': 'تم إغلاق المشروع',
    'project_cancelled': 'تم إلغاء المشروع',
    'project_on_hold': 'المشروع قيد الانتظار',
    'phase1_submitted': 'تم تقديم المرحلة الأولى للمراجعة',
    'phase1_approved': 'تمت الموافقة على المرحلة الأولى',
    'phase1_rejected': 'تم رفض المرحلة الأولى',
    'planning_pending': 'المشروع في انتظار مراجعة التخطيط',
    'planning_approved': 'تمت الموافقة من قِبَل إدارة التخطيط',
    'planning_rejected': 'تم الرفض من قِبَل إدارة التخطيط',
    'pmo_pending': 'المشروع في انتظار مراجعة مكتب إدارة المشاريع',
    'pmo_approved': 'تمت الموافقة من قِبَل مكتب إدارة المشاريع',
    'pmo_rejected': 'تم الرفض من قِبَل مكتب إدارة المشاريع',
    'board_pending': 'المشروع في انتظار مراجعة مجلس المشروع',
    'board_approved': 'تمت الموافقة من قِبَل مجلس المشروع',
    'board_rejected': 'تم الرفض من قِبَل مجلس المشروع',
    'resource_added': 'تمت إضافة مورد',
    'resource_removed': 'تم إزالة مورد',
    'risk_added': 'تم تحديد مخاطرة جديدة',
    'risk_updated': 'تم تحديث المخاطرة',
    'issue_added': 'تم الإبلاغ عن مشكلة جديدة',
    'issue_resolved': 'تم حل المشكلة',
    'change_requested': 'تم تقديم طلب تغيير',
    'change_approved': 'تمت الموافقة على طلب التغيير',
    'change_rejected': 'تم رفض طلب التغيير',
    'document_uploaded': 'تم رفع وثيقة',
    'escalation_created': 'تم تعيين تصعيد جديد',
    'escalation_escalated': 'تم تصعيد المشكلة إلى مستوى أعلى',
    'escalation_reminder': 'تذكير بالتصعيد',
    'escalation_auto_escalated': 'تم التصعيد التلقائي',
    'escalation_closed': 'تم إغلاق التصعيد',
    'benefit_added': 'تمت إضافة فائدة',
    'lesson_learned_added': 'تمت إضافة درس مستفاد',
    'stakeholder_added': 'تمت إضافة صاحب مصلحة',
    'financial_item_added': 'تمت إضافة بند مالي',
    'financial_item_approved': 'تمت الموافقة على البند المالي',
    'financial_item_rejected': 'تم رفض البند المالي',
    'meeting_created': 'اجتماع جديد - يتطلب الموافقة',
    'meeting_approved': 'تمت الموافقة على الاجتماع',
    'meeting_rejected': 'تم رفض الاجتماع',
    'program_created': 'تم إنشاء برنامج جديد',
    'program_submitted': 'تم تقديم البرنامج للمراجعة',
    'program_approved': 'تمت الموافقة على البرنامج',
    'program_rejected': 'تم رفض البرنامج',
    'program_auto_approved': 'تمت الموافقة على البرنامج تلقائياً',
    'program_deleted': 'تم حذف البرنامج',
    'gate_submitted': 'تم تقديم المرحلة للاعتماد',
    'gate_approved': 'تمت الموافقة على المرحلة',
    'gate_rejected': 'تم رفض المرحلة',
    'gate_resubmitted': 'تمت إعادة تقديم المرحلة',
    'strategic_plan_submitted': 'تم تقديم الخطة الاستراتيجية',
    'strategic_plan_approved': 'تمت الموافقة على الخطة الاستراتيجية',
    'strategic_plan_rejected': 'تم رفض الخطة الاستراتيجية',
    'strategic_plan_auto_approved': 'تمت الموافقة التلقائية على الخطة الاستراتيجية',
    'strategic_kpi_submitted': 'تم تقديم مؤشر الأداء الاستراتيجي',
    'strategic_kpi_approved': 'تمت الموافقة على مؤشر الأداء الاستراتيجي',
    'strategic_kpi_rejected': 'تم رفض مؤشر الأداء الاستراتيجي',
    'strategic_kpi_auto_approved': 'تمت الموافقة التلقائية على مؤشر الأداء',
    'kpi_measurement_submitted': 'تم تقديم قياس المؤشر',
    'kpi_measurement_approved': 'تمت الموافقة على قياس المؤشر',
    'kpi_measurement_rejected': 'تم رفض قياس المؤشر',
    'kpi_measurement_auto_approved': 'تمت الموافقة التلقائية على قياس المؤشر',
    'initiative_submitted': 'تم تقديم المبادرة',
    'initiative_approved': 'تمت الموافقة على المبادرة',
    'initiative_rejected': 'تم رفض المبادرة',
    'initiative_auto_approved': 'تمت الموافقة التلقائية على المبادرة',
    'initiative_update_submitted': 'تم تقديم تحديث المبادرة',
    'initiative_update_auto_approved': 'تمت الموافقة التلقائية على تحديث المبادرة',
    'initiative_closure_submitted': 'تم تقديم إغلاق المبادرة',
    'initiative_closure_approved': 'تمت الموافقة على إغلاق المبادرة',
    'initiative_closure_rejected': 'تم رفض إغلاق المبادرة',
    'initiative_closure_auto_approved': 'تمت الموافقة التلقائية على إغلاق المبادرة',
    'initiative_change_request_submitted': 'تم تقديم طلب تغيير المبادرة',
    'initiative_change_request_approved': 'تمت الموافقة على طلب تغيير المبادرة',
    'initiative_change_request_rejected': 'تم رفض طلب تغيير المبادرة',
    'initiative_change_request_auto_approved': 'تمت الموافقة التلقائية على طلب تغيير المبادرة',
    'strategic_risk_submitted': 'تم تقديم المخاطرة الاستراتيجية',
    'strategic_risk_approved': 'تمت الموافقة على المخاطرة الاستراتيجية',
    'strategic_risk_rejected': 'تم رفض المخاطرة الاستراتيجية',
    'strategic_risk_auto_approved': 'تمت الموافقة التلقائية على المخاطرة الاستراتيجية',
    'strategic_challenge_submitted': 'تم تقديم التحدي الاستراتيجي',
    'strategic_challenge_approved': 'تمت الموافقة على التحدي الاستراتيجي',
    'strategic_challenge_rejected': 'تم رفض التحدي الاستراتيجي',
    'strategic_challenge_auto_approved': 'تمت الموافقة التلقائية على التحدي الاستراتيجي',
    'corrective_plan_submitted': 'تم تقديم خطة التصحيح',
    'corrective_plan_approved': 'تمت الموافقة على خطة التصحيح',
    'corrective_plan_rejected': 'تم رفض خطة التصحيح',
    'corrective_plan_auto_approved': 'تمت الموافقة التلقائية على خطة التصحيح',
    'kpi_change_request_submitted': 'تم تقديم طلب تغيير مؤشر الأداء',
    'kpi_change_request_approved': 'تمت الموافقة على طلب تغيير مؤشر الأداء',
    'kpi_change_request_rejected': 'تم رفض طلب تغيير مؤشر الأداء',
    'kpi_change_request_auto_approved': 'تمت الموافقة التلقائية على طلب تغيير مؤشر الأداء',
};

const getLocalizedTitle = (item) => {
    if (currentLanguage.value === 'ar') {
        return item.title_ar || ARABIC_TITLES[item.type] || item.title;
    }
    return item.title;
};

const getLocalizedMessage = (item) => {
    if (currentLanguage.value === 'ar') {
        return item.message_ar || item.message;
    }
    return item.message;
};
</script>

<template>
    <div
        ref="dropdownRef"
        :class="props.class"
        :dir="isRtl ? 'rtl' : 'ltr'"
        class="rounded-2xl bg-surface-0 dark:bg-surface-950 shadow-[0px_129.205px_36.493px_0px_rgba(44,54,87,0.00),_0px_82.849px_33.534px_0px_rgba(44,54,87,0.01),_0px_46.356px_27.616px_0px_rgba(44,54,87,0.03),_0px_20.712px_20.712px_0px_rgba(44,54,87,0.05),_0px_4.932px_11.836px_0px_rgba(44,54,87,0.06)] dark:shadow-stroke"
    >
        <!-- Header -->
        <div :class="['p-4 flex items-center justify-between border-b', isRtl ? 'pr-6' : 'pl-6']">
            <div class="flex items-center gap-2">
                <span class="font-medium">{{ t('Notifications') }}</span>
                <span v-if="unreadCount > 0" class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                    {{ unreadCount > 99 ? '99+' : unreadCount }}
                </span>
            </div>
            <button
                v-if="unreadCount > 0"
                @click.stop="markAllAsRead"
                :disabled="markingAllRead"
                class="px-2 py-1 text-sm rounded-lg border hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors duration-150 disabled:opacity-50"
            >
                {{ markingAllRead ? t('Marking...') : t('Mark all as read') }}
            </button>
        </div>

        <!-- Category Tabs -->
        <div class="p-2 space-y-2">
            <SelectButton v-model="selectedOption" :options="options" option-label="name" option-value="value" pt:root:class="!w-full [&_button]:!flex-1" />

            <!-- Loading State -->
            <div v-if="loading" class="flex items-center justify-center py-8">
                <i class="pi pi-spin pi-spinner text-2xl text-surface-400"></i>
            </div>

            <!-- Empty State -->
            <div v-else-if="!notifications.length" class="flex flex-col items-center justify-center py-8 text-surface-400">
                <i class="pi pi-bell-slash text-4xl mb-2"></i>
                <span class="text-sm">{{ selectedOption === 'archived' ? t('No archived notifications') : t('No new notifications') }}</span>
            </div>

            <!-- Notification List -->
            <div v-else class="max-h-[400px] overflow-y-auto space-y-1">
                <template v-for="item in notifications" :key="item.id">
                    <div
                        @click="handleNotificationClick(item)"
                        :class="[
                            'p-3 flex items-start gap-3 rounded-lg transition-colors duration-150 group',
                            item.is_read
                                ? 'hover:bg-surface-50 dark:hover:bg-surface-900'
                                : 'bg-primary-50/50 dark:bg-primary-900/10 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                        ]"
                    >
                        <!-- Notification Icon -->
                        <div :class="['flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center', getIconBgClass(item)]">
                            <i :class="[getNotificationIcon(item), 'text-white text-sm']"></i>
                        </div>

                        <!-- Content -->
                        <div class="flex-1 min-w-0">
                            <div class="flex items-start justify-between gap-2">
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2">
                                        <div class="flex-1 min-w-0">
                                            <span :class="['block text-sm font-medium truncate', !item.is_read ? 'text-surface-900 dark:text-surface-100' : 'text-surface-600 dark:text-surface-400']">
                                                {{ getLocalizedTitle(item) }}
                                            </span>
                                        </div>
                                        <span v-if="!item.is_read" class="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full"></span>
                                    </div>
                                    <span v-if="getTriggeredByName(item)" class="text-xs text-surface-400">
                                        {{ t('by') }} {{ getTriggeredByName(item) }}
                                    </span>
                                </div>
                                <span class="text-xs text-surface-400 flex-shrink-0 whitespace-nowrap">{{ item.created_at_human }}</span>
                            </div>
                            <div class="mt-0.5 space-y-0.5">
                                <p class="line-clamp-2 text-xs text-surface-500">
                                    {{ getLocalizedMessage(item) }}
                                </p>
                            </div>
                            <!-- Priority + Project -->
                            <div class="mt-1.5 flex items-center gap-2 flex-wrap">
                                <span :class="['inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase', getPriorityClass(item.priority)]">
                                    {{ t(item.priority) }}
                                </span>
                                <span v-if="item.project" class="text-[10px] text-surface-400 flex items-center gap-1">
                                    <i class="pi pi-folder text-[9px]"></i>
                                    {{ item.project.name }}
                                </span>
                            </div>
                            <!-- Warning action text -->
                            <div v-if="item.action === 'warn' && item.action_text" class="mt-1.5 flex items-start gap-2 p-2 ps-3 rounded-xl bg-orange-500/10">
                                <i class="pi pi-exclamation-triangle mt-0.5 text-orange-600 text-xs"></i>
                                <p class="text-xs text-orange-600">{{ item.action_text }}</p>
                            </div>
                        </div>

                        <!-- Action Icons (visible on hover) -->
                        <div class="flex-shrink-0 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                                v-if="!item.is_read"
                                @click.stop="markAsRead(item)"
                                class="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-400 hover:text-surface-600"
                                :title="t('Mark as read')"
                            >
                                <i class="pi pi-check text-xs"></i>
                            </button>
                            <button
                                @click.stop="archiveNotification(item)"
                                class="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-200 dark:hover:bg-surface-700 text-surface-400 hover:text-surface-600"
                                :title="selectedOption === 'archived' ? t('Move to inbox') : t('Archive')"
                            >
                                <i :class="['text-xs', selectedOption === 'archived' ? 'pi pi-inbox' : 'pi pi-bookmark']"></i>
                            </button>
                            <button
                                @click.stop="deleteNotification(item)"
                                class="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-surface-400 hover:text-red-600"
                                :title="t('Delete')"
                            >
                                <i class="pi pi-trash text-xs"></i>
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>
